import { useState } from "react";

import dayjs from "dayjs";
import { Check, Package, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { getProductionUnitLabel } from "~/constants/units";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import type { Pallet } from "~/types/wms";

interface ActivePalletCardProps {
  pallet: Pallet;
  onEndProduction: (palletId: string, boxCount: number) => Promise<void>;
  isEnding: boolean;
  productionUnit?: string;
  expectedQuantity?: number;
  onDiscard?: (palletId: string) => void;
}

export function ActivePalletCard({
  pallet,
  onEndProduction,
  isEnding,
  productionUnit,
  expectedQuantity,
  onDiscard,
}: ActivePalletCardProps) {
  // initial_box_count is an integer column; round the prefill so a stray
  // decimal can never reach the finalized pallet / printed label.
  const [boxCount, setBoxCount] = useState<number>(
    expectedQuantity != null ? Math.round(expectedQuantity) : 0
  );

  const handleEndProduction = async () => {
    if (boxCount <= 0) return;
    await onEndProduction(pallet.id, boxCount);
  };

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <Package className="h-5 w-5" />
          PALLET #{pallet.sequence_number} EN PRODUCCIÓN
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Código:</span>
            <p className="font-mono font-medium">{pallet.code}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Inicio:</span>
            <p className="font-medium">
              {pallet.production_started_at
                ? dayjs(pallet.production_started_at).format("HH:mm")
                : "-"}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="boxCount">
            Cantidad producida ({getProductionUnitLabel(productionUnit, 2)})
          </Label>
          <Input
            id="boxCount"
            type="number"
            min={1}
            value={boxCount}
            onChange={(e) => setBoxCount(parseInt(e.target.value) || 0)}
            className="text-lg font-medium"
          />
        </div>

        <Button
          onClick={handleEndProduction}
          disabled={isEnding || boxCount <= 0}
          className="w-full"
          size="lg"
        >
          {isEnding ? (
            "Finalizando..."
          ) : (
            <>
              <Check className="mr-2 h-5 w-5" />
              Finalizar Pallet
            </>
          )}
        </Button>

        {/* Escape hatch: discard a pallet started by mistake (e.g. an empty one)
            so the operator is never stuck with a pallet they can't finalize. */}
        {onDiscard && (
          <Button
            onClick={() => onDiscard(pallet.id)}
            disabled={isEnding}
            variant="ghost"
            size="sm"
            className="w-full text-red-600 hover:text-red-700"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Descartar pallet
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
